import { createTracker, parseAnalyticsContext } from '@theme/analytics';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { MORPH_OPTIONS, morph } from '@theme/morph';
import { fetchCart, fetchConfig, getCartQtyForVariant } from '@theme/utilities';

/** GTM triggers match these names; they do not follow the quantity setting. */
const EVENTS = /** @type {const} */ ({
  shown: 'buy_six_block_shown',
  clicked: 'buy_six_clicked',
  added: 'buy_six_added',
  failed: 'buy_six_failed',
});

/**
 * @typedef {Object} CartSnapshot
 * @property {number} qty
 * @property {number} total
 */

/**
 * @typedef {Object} UpdateOutcome
 * @property {typeof EVENTS.added | typeof EVENTS.failed} event
 * @property {string} [reason]
 */

/**
 * Classifies a /cart/update.js result. Network and JSON failures are handled
 * by the caller: they never produce a cart to classify.
 *
 * @param {{ items?: Array<{ variant_id: number, quantity: number }>, total_price?: number } | null} cart
 * @param {number} target
 * @param {string} variantId
 * @returns {UpdateOutcome}
 */
function classifyUpdate(cart, target, variantId) {
  if (!cart?.items) {
    return { event: EVENTS.failed, reason: 'cart_error' };
  }

  const qty = getCartQtyForVariant(variantId, cart);

  if (qty === target) return { event: EVENTS.added };
  if (qty === 0) return { event: EVENTS.failed, reason: 'variant_unavailable' };
  if (qty < target) return { event: EVENTS.failed, reason: 'insufficient_stock' };

  return { event: EVENTS.failed, reason: 'other' };
}

/**
 * @param {unknown} body
 * @returns {string}
 */
function cartErrorText(body) {
  if (!body || typeof body !== 'object') return '';

  const payload = /** @type {{ description?: unknown, message?: unknown }} */ (body);
  if (typeof payload.description === 'string' && payload.description) return payload.description;
  if (typeof payload.message === 'string' && payload.message) return payload.message;

  return '';
}

/**
 * Sets the current variant's cart line to a fixed quantity.
 * Eligibility is the presence of the button; this element only applies the cart gate.
 *
 * Rules and invariants: docs/fixed-quantity-button.md
 */
class FixedQuantityButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {CartSnapshot} */
  #cart = { qty: 0, total: 0 };

  #tracker = createTracker(() => parseAnalyticsContext(this.dataset.analytics));

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#seedCart();
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.#trackShownIfVisible();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #seedCart() {
    const qty = Number(this.dataset.cartQty);
    const total = Number(this.dataset.cartTotal);

    this.#cart = {
      qty: Number.isFinite(qty) ? qty : 0,
      total: Number.isFinite(total) ? total : 0,
    };
  }

  #trackShownIfVisible() {
    if (!this.querySelector('button') || this.hidden) return;

    this.#tracker.trackOnce(this.dataset.variantId, EVENTS.shown);
  }

  /**
   * @param {Event} event
   */
  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || !this.contains(button) || button.hasAttribute('aria-busy')) return;

    const variantId = this.dataset.variantId;
    const target = Number(this.dataset.targetQuantity);
    if (!variantId || !Number.isFinite(target)) return;

    const before = {
      qty_in_cart_before: this.#cart.qty,
      cart_value_before: this.#cart.total / 100,
    };

    this.#hideError();
    this.#tracker.track(EVENTS.clicked, before);
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({ updates: { [variantId]: target } }),
        })
      );

      let body;
      try {
        body = await response.json();
      } catch {
        this.#fail(before, 'network_error');
        return;
      }

      if (!response.ok) {
        this.#fail(before, 'cart_error', cartErrorText(body));
        await this.#syncCartAfterError();
        return;
      }

      const outcome = classifyUpdate(body, target, variantId);
      const after = {
        qty_in_cart_after: getCartQtyForVariant(variantId, body),
        cart_value_after: body.total_price / 100,
      };

      if (outcome.event === EVENTS.added) {
        this.#tracker.track(EVENTS.added, { ...before, ...after });
      } else {
        this.#tracker.track(EVENTS.failed, {
          ...before,
          ...after,
          error_reason: outcome.reason,
        });
        this.#showError();
      }

      this.dispatchEvent(
        new CartAddEvent(body, this.id, {
          source: 'fixed-quantity-button',
          variantId,
          itemCount: body.item_count,
        })
      );
    } catch {
      this.#fail(before, 'network_error');
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * @param {CartAddEvent | CartUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.querySelector('button')) return;

    let cart = event.detail?.resource;
    if (!cart?.items) {
      try {
        cart = await fetchCart();
      } catch {
        return;
      }
    }

    if (this.#abort?.signal.aborted || !this.querySelector('button')) return;

    const variantId = this.dataset.variantId ?? '';
    const target = Number(this.dataset.targetQuantity);
    const qty = getCartQtyForVariant(variantId, cart);

    this.#cart = { qty, total: cart.total_price };
    this.hidden = qty >= target;

    if (!this.hidden) this.#trackShownIfVisible();
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    const newHost = html.querySelector(
      `fixed-quantity-button-component[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newHost) return;

    // Default morph options are childrenOnly, which would leave host attributes
    // (hidden, data-variant-id, data-analytics) on the previous variant.
    morph(this, newHost, { ...MORPH_OPTIONS, childrenOnly: false });
    this.#seedCart();
    this.#trackShownIfVisible();
  };

  /**
   * @param {{ qty_in_cart_before: number, cart_value_before: number }} before
   * @param {string} reason
   * @param {string} [message]
   */
  #fail(before, reason, message) {
    this.#tracker.track(EVENTS.failed, { ...before, error_reason: reason });
    this.#showError(message);
  }

  async #syncCartAfterError() {
    try {
      const cart = await fetchCart();
      this.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          source: 'fixed-quantity-button',
          itemCount: cart.item_count,
        })
      );
    } catch {
      // The cart may have changed, but we cannot read it.
    }
  }

  /**
   * @param {string} [message]
   */
  #showError(message) {
    const error = this.querySelector('.fixed-quantity-button__error');
    if (!(error instanceof HTMLElement)) return;

    error.hidden = false;
    error.textContent = message || error.dataset.errorMessage || '';
  }

  #hideError() {
    const error = this.querySelector('.fixed-quantity-button__error');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = '';
    error.hidden = true;
  }
}

if (!customElements.get('fixed-quantity-button-component')) {
  customElements.define('fixed-quantity-button-component', FixedQuantityButton);
}
